"use client";

import { useTranslations } from "next-intl";

import EmptyList from "@/images/empty-list.svg";
import { cn } from "@/utils/cn";

import { EmptyStateProps } from "./types";

/**
 * EmptyState - Unified component to replace repetitive empty list patterns
 *
 * This component provides a consistent empty state design across all listing pages.
 * It includes the empty list icon and customizable messaging with i18n support.
 *
 * @example
 * // Basic usage (uses default translations)
 * <EmptyState />
 *
 * @example
 * // Custom title and message
 * <EmptyState
 *   title="No Results"
 *   message="Try adjusting your search criteria"
 * />
 *
 * @example
 * // With custom styling
 * <EmptyState
 *   className="py-[2rem]"
 *   iconClassName="w-1/2"
 * />
 */
function EmptyState({
  title,
  message,
  className,
  iconClassName,
}: EmptyStateProps) {
  const t = useTranslations();

  // If no custom message, use the standard pattern with translations
  const displayMessage = message || (
    <>
      {t("At your request")},&nbsp;
      <span className="text-red font-medium lowercase">
        {title || t("Not found")}
      </span>
      ,&nbsp;
      <span className="lowercase">
        {t("Change the parameters and try again")}
      </span>
    </>
  );

  return (
    <div className={cn("flex flex-col items-center justify-center", className)}>
      <EmptyList
        className={cn(
          "text-[#D9D9D9] w-2/3 h-auto max-w-screen-xs",
          iconClassName,
        )}
      />
      <div className="text-center max-w-screen-md mx-[2rem] md:mx-0 mt-[3rem] text-[1.6rem] lg:text-[2.3rem] text-gray-910">
        {displayMessage}
      </div>
    </div>
  );
}

export default EmptyState;
export { EmptyState };
